/**
 * Template de prise de besoin : Plan Directeur TI - Analyse Technique & Budget
 * Version pour analyse technique approfondie avec budget détaillé
 */

import type { IntakeTemplate } from '../templateTypes';

export const templatePlanDirecteurTechnique: IntakeTemplate = {
  id: 'plan-directeur-technique-v1',
  name: 'Prise de besoin Plan Directeur TI - Analyse Technique & Budget',
  description:
    'Questionnaire complet pour analyse technique approfondie : infrastructure, équipements, RTO/RPO, sauvegardes, sécurité, budget détaillé, conformité Loi 25.',
  documentTypeCode: 'PLAN_DIRECTEUR_TECHNIQUE',
  version: '1.0.0',
  phases: [
    {
      id: 'phase1',
      title: 'Qualification Client',
      description: 'Informations générales sur l\'entreprise',
      icon: 'Building2',
      questions: [
        {
          id: 'company_name',
          label: "Nom de l'entreprise",
          type: 'text',
          required: true,
        },
        {
          id: 'company_type',
          label: "Type d'entreprise",
          type: 'select',
          options: ['Startup / Entreprise en démarrage', 'PME établie', 'Entreprise en croissance', 'Organisation'],
          required: true,
        },
        {
          id: 'industry',
          label: "Industrie / Secteur d'activité",
          type: 'select',
          options: [
            "Cabinet d'avocats / Juridique",
            'Cabinet comptable / CPA',
            'Services professionnels (Autre)',
            'Manufacturing / Production',
            'Commerce de détail',
            'Santé',
            'Finance / Assurance',
            'Technologies',
            'Construction',
            'Transport / Logistique',
            'Éducation',
            'Autre',
          ],
          required: true,
        },
        {
          id: 'employees',
          label: "Nombre d'employés",
          type: 'select',
          options: ['Moins de 25 employés', '25-49 employés', '50-100 employés', '100-250 employés', '250 employés et plus'],
          required: true,
        },
        {
          id: 'revenue',
          label: "Chiffre d'affaires annuel (approximatif)",
          type: 'select',
          options: ['Moins de 1M$', '1M$ - 3M$', '3M$ - 10M$', '10M$ - 25M$', '25M$ - 50M$', '50M$ et plus', 'À déterminer'],
          required: false,
        },
        {
          id: 'it_maturity',
          label: 'Maturité TI actuelle',
          type: 'select',
          options: [
            'Débutant - Infrastructure basique, peu de systèmes',
            'Intermédiaire - Systèmes en place, mais peu optimisés',
            'Avancé - Infrastructure solide, processus établis',
            'Expert - TI stratégique, innovation continue',
          ],
          required: true,
        },
      ],
    },
    {
      id: 'phase2',
      title: 'Infrastructure & Équipements',
      description: 'Inventaire détaillé de l\'infrastructure et des équipements',
      icon: 'Server',
      questions: [
        {
          id: 'infrastructure_description',
          label: 'Description de l\'infrastructure actuelle',
          type: 'textarea',
          placeholder: 'Décrivez votre infrastructure actuelle : serveurs, stockage, réseau, WiFi, etc.',
          required: true,
        },
        {
          id: 'plan_visio',
          label: 'Plan Visio de l\'infrastructure réseau',
          type: 'textarea',
          placeholder: 'Option 1 : Décrivez textuellement votre infrastructure réseau (serveurs, switches, routeurs, connexions).\n\nOption 2 : Si vous avez un plan Visio, décrivez-le ici ou indiquez que vous le fournirez séparément.\n\nFormat suggéré :\n- Serveur principal : [description]\n- Switches : [description]\n- Routeurs : [description]\n- Connexions : [description]',
          required: false,
          helpText: 'Si vous avez un fichier Visio, vous pouvez le décrire ici ou le fournir séparément. Le plan sera intégré dans le document final.',
        },
        {
          id: 'serveurs_liste',
          label: 'Liste des serveurs (format Excel/Tableau)',
          type: 'textarea',
          placeholder: 'Listez vos serveurs dans ce format (une ligne par serveur) :\n\nModèle | Spécifications | Garantie | Date achat | Fin de vie prévue | Notes\nExemple :\nDell PowerEdge R740 | 2x Xeon, 128GB RAM, 2TB SSD | 5 ans | 2022-01-15 | 2027-01-15 | Serveur principal ERP',
          required: false,
          helpText: 'Vous pouvez copier-coller depuis Excel ou saisir manuellement. Format : colonnes séparées par | (pipe).',
        },
        {
          id: 'stockage_liste',
          label: 'Liste des unités de stockage (NAS, SAN, etc.)',
          type: 'textarea',
          placeholder: 'Listez vos unités de stockage :\n\nType | Modèle | Capacité | Configuration | Notes\nExemple :\nNAS | Synology DS1821+ | 32TB | RAID 6 | Sauvegarde locale',
          required: false,
        },
        {
          id: 'ups_liste',
          label: 'Liste des onduleurs/UPS',
          type: 'textarea',
          placeholder: 'Listez vos onduleurs :\n\nModèle | Capacité | Batteries | Date installation | Notes',
          required: false,
        },
        {
          id: 'reseau_equipements',
          label: 'Équipements réseau (switches, routeurs, WiFi)',
          type: 'textarea',
          placeholder: 'Listez vos équipements réseau :\n\nType | Modèle | Ports | Configuration | Notes\nExemple :\nSwitch | Cisco Catalyst 2960 | 24 ports POE | VLAN configuré | Bureau principal',
          required: false,
        },
        {
          id: 'postes_travail',
          label: 'Postes de travail',
          type: 'textarea',
          placeholder: 'Quantité et types de postes :\n\nType | Quantité | Âge moyen | Remplacement prévu | Notes\nExemple :\nDesktop | 25 | 3 ans | 2025 | Windows 11',
          required: false,
        },
      ],
    },
    {
      id: 'phase3',
      title: 'RTO & RPO',
      description: 'Objectifs de récupération et de point de restauration',
      icon: 'Clock',
      questions: [
        {
          id: 'rto_rpo_explanation',
          label: 'Compréhension RTO/RPO',
          type: 'textarea',
          placeholder: 'Avez-vous déjà défini vos RTO/RPO ? Si oui, décrivez-les. Si non, laissez vide et nous proposerons des valeurs recommandées.',
          required: false,
          helpText: 'RPO = Perte de données maximale acceptable. RTO = Temps maximum pour restaurer un service.',
        },
        {
          id: 'rto_rpo_table',
          label: 'Tableau RTO/RPO par service',
          type: 'textarea',
          placeholder: 'Remplissez ce tableau (une ligne par service) :\n\nService | RPO (heures) | RTO (heures) | Notes\nExemple :\nCourriels | 1 | 2 | Service critique\nDonnées actives | 4 | 8 | Word, Excel, PDF\nApplicatifs comptables | 2 | 4 | ERP critique',
          required: false,
          helpText: 'Vous pouvez copier-coller depuis Excel ou saisir manuellement. Format : colonnes séparées par | (pipe).',
        },
      ],
    },
    {
      id: 'phase4',
      title: 'Sauvegardes & Hébergement',
      description: 'Stratégies de sauvegardes et hébergement',
      icon: 'HardDrive',
      questions: [
        {
          id: 'environnement_type',
          label: 'Type d\'environnement',
          type: 'select',
          options: [
            'Microsoft 365 uniquement',
            'Microsoft 365 et Cloud BZ',
            'Microsoft 365 et infrastructure locale',
            'Autre (préciser)',
          ],
          required: true,
        },
        {
          id: 'sauvegardes_actuelles',
          label: 'Stratégies de sauvegardes actuelles',
          type: 'textarea',
          placeholder: 'Décrivez vos stratégies de sauvegardes actuelles :\n- Fréquence\n- Localisation\n- Type (locale, cloud, archivage)\n- Outils utilisés',
          required: false,
        },
        {
          id: 'sauvegardes_desirees',
          label: 'Stratégies de sauvegardes désirées',
          type: 'textarea',
          placeholder: 'Décrivez vos stratégies de sauvegardes désirées ou laissez vide pour recommandations.',
          required: false,
        },
        {
          id: 'hebergement_physique',
          label: 'Hébergement physique des équipements',
          type: 'textarea',
          placeholder: 'Décrivez où sont hébergés vos équipements :\n- Localisation (bureau, datacenter, cloud)\n- Conditions environnementales\n- Sécurité physique\n- Redondance géographique (si applicable)',
          required: false,
        },
      ],
    },
    {
      id: 'phase5',
      title: 'Sécurité & Conformité',
      description: 'Fonctionnalités de sécurité et conformité Loi 25',
      icon: 'Shield',
      questions: [
        {
          id: 'antivirus_edr',
          label: 'Antivirus EDR',
          type: 'select',
          options: ['Actuel', 'Désiré', 'Non applicable'],
          required: false,
        },
        {
          id: 'antispam',
          label: 'Antispam avancé',
          type: 'select',
          options: ['Actuel', 'Désiré', 'Non applicable'],
          required: false,
        },
        {
          id: 'authentification_2fa',
          label: 'Authentification à facteurs multiples (2FA)',
          type: 'select',
          options: ['Actuel', 'Désiré', 'Non applicable'],
          required: false,
        },
        {
          id: 'intune',
          label: 'Intune (gestion des appareils)',
          type: 'select',
          options: ['Actuel', 'Désiré', 'Non applicable'],
          required: false,
        },
        {
          id: 'loi25_conformite',
          label: 'Conformité Loi 25',
          type: 'textarea',
          placeholder: 'Répondez aux questions suivantes (Oui/Non pour chacune) :\n\n1. Personne responsable de la protection des renseignements personnels nommée ?\n2. Politique de confidentialité publiée sur le site Web ?\n3. Politique de conservation des données adoptée ?\n4. Procédures pour le traitement des plaintes/demandes ?\n5. Procédure pour l\'évaluation des facteurs relatifs à la vie privée (EFVP) ?\n6. Formulaires de consentement révisés et conformes ?\n7. Plan d\'intervention en cas d\'incident de confidentialité ?\n8. Registre pour les incidents de confidentialité créé ?',
          required: false,
        },
      ],
    },
    {
      id: 'phase6',
      title: 'Logiciels & Licences',
      description: 'Inventaire des logiciels et licences',
      icon: 'FileText',
      questions: [
        {
          id: 'logiciels_liste',
          label: 'Liste des logiciels et licences',
          type: 'textarea',
          placeholder: 'Listez vos logiciels et licences :\n\nLogiciel | Version | Quantité licences | Type licence | Renouvellement | Notes\nExemple :\nWindows Server | 2022 | 2 | Standard | Annuel | Serveurs principaux\nMicrosoft 365 | E3 | 50 | Utilisateur | Mensuel | Tous les employés\nSQL Server | 2022 | 1 | Standard | Annuel | Base de données ERP',
          required: false,
          helpText: 'Vous pouvez copier-coller depuis Excel ou saisir manuellement. Format : colonnes séparées par | (pipe).',
        },
        {
          id: 'licences_cal',
          label: 'CAL (Client Access License)',
          type: 'textarea',
          placeholder: 'Détails des CAL :\n- CAL Windows : [quantité]\n- CAL Terminal : [quantité]\n- Autres CAL : [détails]',
          required: false,
        },
      ],
    },
    {
      id: 'phase7',
      title: 'Budget & Investissements',
      description: 'Budget détaillé et investissements prévus',
      icon: 'DollarSign',
      questions: [
        {
          id: 'budget_actuel',
          label: 'Budget TI actuel (annuel)',
          type: 'text',
          placeholder: 'Montant approximatif en $',
          required: false,
        },
        {
          id: 'budget_categories',
          label: 'Répartition budgétaire actuelle',
          type: 'textarea',
          placeholder: 'Répartition approximative par catégorie :\n- Matériel infrastructure : [% ou $]\n- Logiciels et licences : [% ou $]\n- Services TI : [% ou $]\n- Postes et équipement : [% ou $]',
          required: false,
        },
        {
          id: 'budget_vision_5ans',
          label: 'Budget vision 5 ans (si disponible)',
          type: 'textarea',
          placeholder: 'Si vous avez un budget prévisionnel sur 5 ans, décrivez-le ici ou laissez vide pour recommandations.',
          required: false,
        },
        {
          id: 'investissements_prevus',
          label: 'Investissements majeurs prévus',
          type: 'textarea',
          placeholder: 'Listez les investissements majeurs prévus :\n- Remplacement serveurs : [année, montant estimé]\n- Migration cloud : [année, montant estimé]\n- Modernisation réseau : [année, montant estimé]',
          required: false,
        },
      ],
    },
    {
      id: 'phase8',
      title: 'Programmation, Télécom & Autres',
      description: 'Besoins en programmation, télécom et autres équipements',
      icon: 'Code',
      questions: [
        {
          id: 'programmation_besoins',
          label: 'Besoins en programmation',
          type: 'textarea',
          placeholder: 'Décrivez vos besoins en programmation :\n- Applications personnalisées nécessaires\n- Intégrations requises\n- Maintenance applicative\n- Autres besoins',
          required: false,
        },
        {
          id: 'telecom_infrastructure',
          label: 'Infrastructure télécom',
          type: 'textarea',
          placeholder: 'Décrivez votre infrastructure télécom :\n- Téléphonie (VoIP, Teams, etc.)\n- Connexions internet (bande passante, redondance)\n- Autres services télécom',
          required: false,
        },
        {
          id: 'autres_equipements',
          label: 'Autres équipements ou besoins',
          type: 'textarea',
          placeholder: 'Autres équipements ou besoins non couverts précédemment :\n- Imprimantes, scanners\n- Équipements périphériques\n- Services additionnels',
          required: false,
        },
      ],
    },
    {
      id: 'phase9',
      title: 'Formation & Sensibilisation',
      description: 'Formation et sensibilisation cybersécurité',
      icon: 'Users',
      questions: [
        {
          id: 'formation_cybersecurite',
          label: 'Formation et sensibilisation cybersécurité',
          type: 'textarea',
          placeholder: 'Décrivez vos besoins en formation :\n- Sensibilisation cybersécurité\n- Formation Microsoft 365\n- Autres formations TI\n- Fréquence souhaitée',
          required: false,
        },
        {
          id: 'culture_securite',
          label: 'Culture de sécurité actuelle',
          type: 'select',
          options: [
            'Débutante - Peu de sensibilisation',
            'En développement - Quelques formations',
            'Établie - Formation régulière',
            'Mature - Culture de sécurité forte',
          ],
          required: false,
        },
      ],
    },
  ],
};

